import { useState, useRef } from 'react';
import '../styles/App.css';
import '../styles/ListMyBusiness.css';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import { useTranslation } from "react-i18next";
import { isValidEmail } from "../utils/functions";

const catPhotoPath = '/Images/ListMyBusinessPage.jpeg';

function ListMyBusinessPage() {
    const [t] = useTranslation();
    const [errorMessage, setErrorMessage] = useState("");
    const [phoneCode, setPhoneCode] = useState("+852");
    const [termsAccepted, setTermsAccepted] = useState(false);

    const [formData, setFormData] = useState({
        contactPerson: '',
        phoneNumber: '',
        companyNameEN: '',
        companyNameZH: '',
        email: '',
        website: '',
        facebook: '',
        instagram: '',
        descriptionEN: '',
        descriptionZH: '',
        uploadLogo: null,
        uploadBanner: null,
    });

    const [filePreviews, setFilePreviews] = useState({
        uploadLogo: null,
        uploadBanner: null
    });

    const [dragActive, setDragActive] = useState({
        uploadLogo: false,
        uploadBanner: false
    });

    const logoInputRef = useRef(null);
    const bannerInputRef = useRef(null);

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (value.trim() !== '') {
            setErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handleDrag = (e, fieldName) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(prev => ({ ...prev, [fieldName]: true }));
        } else if (e.type === "dragleave") {
            setDragActive(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const handleDrop = (e, fieldName) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(prev => ({ ...prev, [fieldName]: false }));
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            handleFile(file, fieldName);
        }
    };

    const handleFileChange = (e, fieldName) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            handleFile(file, fieldName);
        }
    };

    const handleFile = (file, fieldName) => {
        if (!file.type.match('image.*')) {
            setErrorMessage("Please upload an image file (JPEG, JPG, PNG)");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setErrorMessage("File size should be less than 5MB");
            return;
        }

        setFormData(prev => ({ ...prev, [fieldName]: file }));
        setErrors(prev => ({ ...prev, [fieldName]: false }));
        setErrorMessage("");

        const reader = new FileReader();
        reader.onload = () => {
            setFilePreviews(prev => ({ ...prev, [fieldName]: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const triggerFileInput = (fieldName) => {
        if (fieldName === 'uploadLogo') {
            logoInputRef.current.click();
        } else if (fieldName === 'uploadBanner') {
            bannerInputRef.current.click();
        }
    };

    const removeFile = (fieldName) => {
        setFormData(prev => ({ ...prev, [fieldName]: null }));
        setFilePreviews(prev => ({ ...prev, [fieldName]: null }));
        setErrors(prev => ({ ...prev, [fieldName]: true }));
    };

    const handlePhoneCodeChange = (e) => {
        setPhoneCode(e.target.value);
    };

    const handleTermsChange = (e) => {
        setTermsAccepted(e.target.checked);
        setErrors(prev => ({ ...prev, termsAccepted: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {
            contactPerson: !formData.contactPerson.trim(),
            phoneNumber: !formData.phoneNumber.trim(),
            companyNameEN: !formData.companyNameEN.trim(),
            companyNameZH: !formData.companyNameZH.trim(),
            email: !formData.email.trim(),
            website: !formData.website.trim(),
            descriptionEN: !formData.descriptionEN.trim(),
            descriptionZH: !formData.descriptionZH.trim(),
            termsAccepted: !termsAccepted,
        };

        setErrors(newErrors);
        const hasErrors = Object.values(newErrors).some(Boolean);
        if (hasErrors) {
            setErrorMessage(t("list_my_business.required_fields_error"));
            return;
        }

        if (!isValidEmail(formData.email)) {
            setErrorMessage(t("list_my_business.email_error"));
            return;
        }

        if (isNaN(formData.phoneNumber.replace(/\D/g, ''))) {
            setErrorMessage(t("list_my_business.number_error"));
            return;
        }

        const payload = new FormData();
        const data = {
            contactPerson: formData.contactPerson,
            phoneNumber: `${phoneCode}${formData.phoneNumber.replace(/\D/g, '')}`,
            companyNameEN: formData.companyNameEN,
            companyNameZH: formData.companyNameZH,
            email: formData.email,
            website: formData.website,
            facebook: formData.facebook,
            instagram: formData.instagram,
            descriptionEN: formData.descriptionEN,
            descriptionZH: formData.descriptionZH,
        };

        payload.append('data', JSON.stringify(data));
        
        if (formData.uploadLogo) {
            payload.append('files.uploadLogo', formData.uploadLogo);
        }
        
        if (formData.uploadBanner) {
            payload.append('files.uploadBanner', formData.uploadBanner);
        }

        try {
            const response = await fetch('https://backend.activikid.hk/api/businesses', {
                method: 'POST',
                body: payload,
            });

            const responseData = await response.json();

            if (!response.ok) {
                console.error('Backend error:', responseData);
                setErrorMessage(
                    responseData.error?.message || 
                    responseData.message || 
                    "Submission failed. Please try again."
                );
                return;
            }

            setErrorMessage("");
            alert("Form submitted successfully!");

            setFormData({
                contactPerson: '',
                phoneNumber: '',
                companyNameEN: '',
                companyNameZH: '',
                email: '',
                website: '',
                facebook: '',
                instagram: '',
                descriptionEN: '',
                descriptionZH: '',
                uploadLogo: null,
                uploadBanner: null,
            });
            
            setFilePreviews({
                uploadLogo: null,
                uploadBanner: null
            });
            
            setTermsAccepted(false);

        } catch (error) {
            console.error('Network error:', error);
            setErrorMessage("Network error - could not connect to server");
        }
    };

    return (
        <>
            <Navbar />
            <div className="form-outer-container">
                <div className="container form-container">
                    <div className="header-image-wrapper">
                        <img 
                            src={catPhotoPath} 
                            alt="List your business on ActiviKid" 
                            className="header-image"
                        />
                    </div>

                    <h2 className="form-title text-center">{t('list_my_business.title')}</h2>
                    <p className="form-subtitle text-center">{t('list_my_business.subtitle')}</p>
         
                    <form onSubmit={handleSubmit} noValidate encType="multipart/form-data">
                        <div className="row">
                            <div className="col-md-6">
                                <label>{t('list_my_business.contact_person')} <span className="required-star">*</span></label>
                                <input
                                    className={`form-input ${errors.contactPerson ? 'input-error' : ''}`}
                                    name="contactPerson"
                                    value={formData.contactPerson}
                                    onChange={handleChange}
                                    placeholder="e.g. Jason Chan"
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label>{t('list_my_business.phone_number')} <span className="required-star">*</span></label>
                                <div className={`phone-input-group ${errors.phoneNumber ? 'input-error' : ''}`}>
                                    <select
                                        className="phone-code-select"
                                        value={phoneCode}
                                        onChange={handlePhoneCodeChange}
                                    >
                                        <option value="+852">+852</option>
                                        <option value="+853">+853</option>
                                        <option value="+86">+86</option>
                                    </select>
                                    <span className="phone-divider">|</span>
                                    <input
                                        className="phone-number-input"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="e.g. 9123 4567"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="row">
                                    <div className="col-md-6">
                                        <label>{t('list_my_business.company_name_en')} <span className="required-star">*</span></label>
                                        <input
                                            className={`form-input ${errors.companyNameEN ? 'input-error' : ''}`}
                                            name="companyNameEN"
                                            value={formData.companyNameEN}
                                            onChange={handleChange}
                                            placeholder="ABC Space"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>{t('list_my_business.company_name_zh')} <span className="required-star">*</span></label>
                                        <input
                                            className={`form-input ${errors.companyNameZH ? 'input-error' : ''}`}
                                            name="companyNameZH"
                                            value={formData.companyNameZH}
                                            onChange={handleChange}
                                            placeholder="e.g. ABC空間"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <label>{t('list_my_business.email')} <span className="required-star">*</span></label>
                                <input
                                    className={`form-input ${errors.email ? 'input-error' : ''}`}
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="e.g. cooperatewithus@gmail.com"
                                    required
                                />
                            </div>
                            
                            <div className="col-md-6">
                                <label>{t('list_my_business.website')} <span className="required-star">*</span></label>
                                <input
                                    className={`form-input ${errors.website ? 'input-error' : ''}`}
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    placeholder="e.g. www.abcspace.hk"
                                    required
                                />
                            </div>
                            
                            <div className="col-md-6">
                                <div className="row">
                                    <div className="col-md-6">
                                        <label>{t('list_my_business.facebook')}</label>
                                        <input
                                            className="form-input"
                                            name="facebook"
                                            value={formData.facebook}
                                            onChange={handleChange}
                                            placeholder="e.g. fb.com/ABC"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>{t('list_my_business.instagram')}</label>
                                        <input
                                            className="form-input"
                                            name="instagram"
                                            value={formData.instagram}
                                            onChange={handleChange}
                                            placeholder="e.g. instagram.com/ABC"
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <label>{t('list_my_business.description_en')} <span className="required-star">*</span></label>
                                <textarea
                                    className={`form-input ${errors.descriptionEN ? 'input-error' : ''}`}
                                    name="descriptionEN"
                                    rows="8"
                                    value={formData.descriptionEN}
                                    onChange={handleChange}
                                    placeholder="Briefly tell us about your company!..."
                                    required
                                ></textarea>
                            </div>
                            <div className="col-md-6">
                                <label>{t('list_my_business.description_zh')} <span className="required-star">*</span></label>
                                <textarea
                                    className={`form-input ${errors.descriptionZH ? 'input-error' : ''}`}
                                    name="descriptionZH"
                                    rows="8"
                                    value={formData.descriptionZH}
                                    onChange={handleChange}
                                    placeholder="來簡單分享一下你的公司吧！"
                                    required
                                ></textarea>
                            </div>
                            
                            <div className="col-12">
                                <label>{t('list_my_business.upload_logo')} <small>{t('list_my_business.logo_size_hint')}</small></label>
                                <div 
                                    className={`upload-box ${errors.uploadLogo ? 'upload-error' : ''} ${dragActive.uploadLogo ? 'drag-active' : ''}`}
                                    onDragEnter={(e) => handleDrag(e, 'uploadLogo')}
                                    onDragLeave={(e) => handleDrag(e, 'uploadLogo')}
                                    onDragOver={(e) => handleDrag(e, 'uploadLogo')}
                                    onDrop={(e) => handleDrop(e, 'uploadLogo')}
                                    onClick={() => triggerFileInput('uploadLogo')}
                                >
                                    {filePreviews.uploadLogo ? (
                                        <div className="file-preview-container">
                                            <img src={filePreviews.uploadLogo} alt="Logo preview" className="file-preview" />
                                            <button 
                                                type="button" 
                                                className="remove-file-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeFile('uploadLogo');
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="upload-instructions">
                                            <svg className="upload-icon" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14.9264 38.6802H11.0863C5.2063 38.2602 2.56641 33.7402 2.56641 29.7202C2.56641 25.7002 5.20633 21.1602 10.9863 20.7602C11.8063 20.6802 12.5263 21.3202 12.5863 22.1602C12.6463 22.9802 12.0264 23.7002 11.1864 23.7602C7.3064 24.0402 5.56641 26.9602 5.56641 29.7402C5.56641 32.5202 7.3064 35.4402 11.1864 35.7202H14.9264C15.7464 35.7202 16.4264 36.4002 16.4264 37.2202C16.4264 38.0402 15.7464 38.6802 14.9264 38.6802Z" fill="#2F4C72"/>
                                                <path d="M33.3456 38.68C33.3057 38.68 33.2857 38.68 33.2457 38.68C32.4257 38.68 31.6657 38 31.6657 37.18C31.6657 36.32 32.3057 35.68 33.1457 35.68C35.6057 35.68 37.8057 34.82 39.5257 33.28C42.6457 30.56 42.8457 26.64 42.0057 23.88C41.1657 21.14 38.8257 18 34.7457 17.5C34.0857 17.42 33.5656 16.92 33.4456 16.26C32.6456 11.46 30.0657 8.14001 26.1457 6.94001C22.1057 5.68001 17.3856 6.92 14.4456 10C11.5856 12.98 10.9256 17.16 12.5856 21.76C12.8656 22.54 12.4657 23.4 11.6857 23.68C10.9057 23.96 10.0457 23.56 9.7657 22.78C7.7457 17.14 8.66572 11.74 12.2857 7.94001C15.9857 4.06001 21.9257 2.51999 27.0257 4.07999C31.7057 5.51999 35.0056 9.37999 36.2056 14.74C40.2856 15.66 43.5657 18.76 44.8657 23.04C46.2857 27.7 45.0057 32.5 41.5057 35.54C39.2857 37.54 36.3857 38.68 33.3456 38.68Z" fill="#2F4C72"/>
                                                <path d="M24.0059 44.5601C19.9859 44.5601 16.2259 42.4201 14.1659 38.9601C13.9459 38.6201 13.7259 38.2201 13.5459 37.7801C12.8659 36.3601 12.5059 34.7401 12.5059 33.0601C12.5059 26.7201 17.6659 21.5601 24.0059 21.5601C30.3459 21.5601 35.5059 26.7201 35.5059 33.0601C35.5059 34.7601 35.1459 36.3601 34.4259 37.8401C34.2659 38.2201 34.0459 38.6201 33.8059 39.0001C31.7859 42.4201 28.0259 44.5601 24.0059 44.5601ZM24.0059 24.5601C19.3259 24.5601 15.5059 28.3801 15.5059 33.0601C15.5059 34.3001 15.7659 35.4601 16.2659 36.5201C16.4259 36.8601 16.5658 37.1401 16.7258 37.4001C18.2458 39.9801 21.0258 41.5601 23.9858 41.5601C26.9458 41.5601 29.7258 39.9801 31.2258 37.4401C31.4058 37.1401 31.5659 36.86 31.6859 36.58C32.2259 35.48 32.4858 34.32 32.4858 33.08C32.5058 28.38 28.6859 24.5601 24.0059 24.5601Z" fill="#2F4C72"/>
                                                <path d="M22.8657 36.5399C22.4857 36.5399 22.1057 36.3999 21.8057 36.0999L19.8256 34.1199C19.2456 33.5399 19.2456 32.5799 19.8256 31.9999C20.4056 31.4199 21.3656 31.4199 21.9456 31.9999L22.9057 32.9599L26.1057 29.9999C26.7257 29.4399 27.6657 29.4799 28.2257 30.0799C28.7857 30.6799 28.7457 31.6399 28.1457 32.1999L23.8857 36.1399C23.5857 36.3999 23.2257 36.5399 22.8657 36.5399Z" fill="#2F4C72"/>
                                            </svg>
                                            <p style={{ color: '#59708E' }}>{t('list_my_business.choose_file')}</p>
                                            <p className="upload-hint">{t('list_my_business.upload_hint')}</p>
                                        </div>
                                    )}
                                    <input
                                        ref={logoInputRef}
                                        className="file-input"
                                        type="file"
                                        name="uploadLogo"
                                        accept="image/png, image/jpeg, image/jpg"
                                        onChange={(e) => handleFileChange(e, 'uploadLogo')}
                                        style={{ display: 'none' }}
                                    />
                                    <button 
                                        type="button" 
                                        className="browse-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput('uploadLogo');
                                        }}
                                    >
                                        {t('list_my_business.browse_file')}
                                    </button>
                                </div>
                            </div>

                            <div className="col-12">
                                <label>{t('list_my_business.upload_banner')} <small>{t('list_my_business.banner_size_hint')}</small></label>
                                <div 
                                    className={`upload-box ${errors.uploadBanner ? 'upload-error' : ''} ${dragActive.uploadBanner ? 'drag-active' : ''}`}
                                    onDragEnter={(e) => handleDrag(e, 'uploadBanner')}
                                    onDragLeave={(e) => handleDrag(e, 'uploadBanner')}
                                    onDragOver={(e) => handleDrag(e, 'uploadBanner')}
                                    onDrop={(e) => handleDrop(e, 'uploadBanner')}
                                    onClick={() => triggerFileInput('uploadBanner')}
                                >
                                    {filePreviews.uploadBanner ? (
                                        <div className="file-preview-container">
                                            <img src={filePreviews.uploadBanner} alt="Banner preview" className="file-preview" />
                                            <button 
                                                type="button" 
                                                className="remove-file-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeFile('uploadBanner');
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="upload-instructions">
                                            <svg className="upload-icon" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14.9264 38.6802H11.0863C5.2063 38.2602 2.56641 33.7402 2.56641 29.7202C2.56641 25.7002 5.20633 21.1602 10.9863 20.7602C11.8063 20.6802 12.5263 21.3202 12.5863 22.1602C12.6463 22.9802 12.0264 23.7002 11.1864 23.7602C7.3064 24.0402 5.56641 26.9602 5.56641 29.7402C5.56641 32.5202 7.3064 35.4402 11.1864 35.7202H14.9264C15.7464 35.7202 16.4264 36.4002 16.4264 37.2202C16.4264 38.0402 15.7464 38.6802 14.9264 38.6802Z" fill="#2F4C72"/>
                                                <path d="M33.3456 38.68C33.3057 38.68 33.2857 38.68 33.2457 38.68C32.4257 38.68 31.6657 38 31.6657 37.18C31.6657 36.32 32.3057 35.68 33.1457 35.68C35.6057 35.68 37.8057 34.82 39.5257 33.28C42.6457 30.56 42.8457 26.64 42.0057 23.88C41.1657 21.14 38.8257 18 34.7457 17.5C34.0857 17.42 33.5656 16.92 33.4456 16.26C32.6456 11.46 30.0657 8.14001 26.1457 6.94001C22.1057 5.68001 17.3856 6.92 14.4456 10C11.5856 12.98 10.9256 17.16 12.5856 21.76C12.8656 22.54 12.4657 23.4 11.6857 23.68C10.9057 23.96 10.0457 23.56 9.7657 22.78C7.7457 17.14 8.66572 11.74 12.2857 7.94001C15.9857 4.06001 21.9257 2.51999 27.0257 4.07999C31.7057 5.51999 35.0056 9.37999 36.2056 14.74C40.2856 15.66 43.5657 18.76 44.8657 23.04C46.2857 27.7 45.0057 32.5 41.5057 35.54C39.2857 37.54 36.3857 38.68 33.3456 38.68Z" fill="#2F4C72"/>
                                                <path d="M24.0059 44.5601C19.9859 44.5601 16.2259 42.4201 14.1659 38.9601C13.9459 38.6201 13.7259 38.2201 13.5459 37.7801C12.8659 36.3601 12.5059 34.7401 12.5059 33.0601C12.5059 26.7201 17.6659 21.5601 24.0059 21.5601C30.3459 21.5601 35.5059 26.7201 35.5059 33.0601C35.5059 34.7601 35.1459 36.3601 34.4259 37.8401C34.2659 38.2201 34.0459 38.6201 33.8059 39.0001C31.7859 42.4201 28.0259 44.5601 24.0059 44.5601ZM24.0059 24.5601C19.3259 24.5601 15.5059 28.3801 15.5059 33.0601C15.5059 34.3001 15.7659 35.4601 16.2659 36.5201C16.4259 36.8601 16.5658 37.1401 16.7258 37.4001C18.2458 39.9801 21.0258 41.5601 23.9858 41.5601C26.9458 41.5601 29.7258 39.9801 31.2258 37.4401C31.4058 37.1401 31.5659 36.86 31.6859 36.58C32.2259 35.48 32.4858 34.32 32.4858 33.08C32.5058 28.38 28.6859 24.5601 24.0059 24.5601Z" fill="#2F4C72"/>
                                                <path d="M22.8657 36.5399C22.4857 36.5399 22.1057 36.3999 21.8057 36.0999L19.8256 34.1199C19.2456 33.5399 19.2456 32.5799 19.8256 31.9999C20.4056 31.4199 21.3656 31.4199 21.9456 31.9999L22.9057 32.9599L26.1057 29.9999C26.7257 29.4399 27.6657 29.4799 28.2257 30.0799C28.7857 30.6799 28.7457 31.6399 28.1457 32.1999L23.8857 36.1399C23.5857 36.3999 23.2257 36.5399 22.8657 36.5399Z" fill="#2F4C72"/>
                                            </svg>
                                            <p style={{ color: '#59708E' }}>{t('list_my_business.choose_file')}</p>
                                            <p className="upload-hint">{t('list_my_business.upload_hint')}</p>
                                        </div>
                                    )}
                                    <input
                                        ref={bannerInputRef}
                                        className="file-input"
                                        type="file"
                                        name="uploadBanner"
                                        accept="image/png, image/jpeg, image/jpg"
                                        onChange={(e) => handleFileChange(e, 'uploadBanner')}
                                        style={{ display: 'none' }}
                                    />
                                    <button 
                                        type="button" 
                                        className="browse-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput('uploadBanner');
                                        }}
                                    >
                                        {t('list_my_business.browse_file')}
                                    </button>
                                </div>
                            </div>
                            
                            <div className="col-12">
                                <div className={`terms-container ${errors.termsAccepted ? 'terms-error' : ''}`}>
                                    <label className="terms-label">
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsAccepted}
                                            onChange={handleTermsChange}
                                            required
                                        />
                                        <span className="terms-custom-checkbox"></span>
                                        <span className="terms-text">
                                            {t('list_my_business.terms_text')}
                                        </span>
                                    </label>
                                </div>
                            </div>
                            
                            {errorMessage && (
                                <div className="error-message">{errorMessage}</div>
                            )}
                            
                            <div className="col-12 text-center">
                                <button type="submit" className="submit-button">
                                    {t('list_my_business.submit')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default ListMyBusinessPage;